'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import type { TpslBasisMode } from './types';

const BasisDropdown = createDropdownSelect<TpslBasisMode>();

export interface BasisSelectProps {
  mode: TpslBasisMode;
  modes: ReadonlyArray<TpslBasisMode>;
  onModeChange: (mode: TpslBasisMode) => void;
  quoteCurrency: string;
  showDescriptions?: boolean;
  disabled?: boolean;
}

type BasisCopy = {
  title: string;
  description: string;
};

export const BasisSelect = ({
  mode,
  modes,
  onModeChange,
  quoteCurrency,
  showDescriptions = true,
  disabled,
}: BasisSelectProps) => {
  const t = useTranslations();

  const copyByMode = useMemo((): Record<TpslBasisMode, BasisCopy> => {
    // Parens/units stay outside i18n so locale copy is just the label word.
    return {
      price: {
        title: `${t('Common.r250923.Price')}(${quoteCurrency})`,
        description: t('Trade.r260720.Tp_sl_price_desc'),
      },
      change: {
        title: `${t('Autogen_common.r251126.s_Change')}(%)`,
        description: t('Trade.r260722.Tp_sl_change_desc'),
      },
      roi: {
        title: `${t('Common.r251127.Roi')}(%)`,
        description: t('Trade.r260720.Tp_sl_roi_desc'),
      },
      pnl: {
        title: `${t('Common.r251107.Pnl')}(${quoteCurrency})`,
        description: t('Trade.r260720.Tp_sl_pnl_desc'),
      },
    };
  }, [quoteCurrency, t]);

  const selectedTitle = copyByMode[mode].title;

  return (
    <BasisDropdown.Root selected={mode} onChange={onModeChange}>
      <BasisDropdown.Trigger asChild>
        <button
          type='button'
          disabled={disabled}
          className={hstack({
            gap: 4,
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
          })}
        >
          <span className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-4' })}>{selectedTitle}</span>
          <ChevronDownIcon size={16} color={token('colors.content.default.level-4')} />
        </button>
      </BasisDropdown.Trigger>
      <BasisDropdown.Select portal css={{ minW: 220 }}>
        {modes.map((itemMode) => {
          const copy = copyByMode[itemMode];
          return (
            <BasisDropdown.Item
              key={itemMode}
              value={itemMode}
              label={copy.title}
              subLabel={showDescriptions ? copy.description : undefined}
            />
          );
        })}
      </BasisDropdown.Select>
    </BasisDropdown.Root>
  );
};
